/**
 * RoleTileGrid — a pre-configured `MobileTileGrid` for non-admin dashboards
 * (GM + Accounts). Renders a tile per tab key passed in, plus a Quick Builder
 * shortcut, plus role-appropriate "Clock Status / My Profile" tiles. Keeps the
 * mobile dashboard navigation logic in one place so GM and Accounts don't drift
 * apart from CompanyDashboard.
 *
 * Props:
 *   - role:           current user role (used to filter optional tiles)
 *   - tabs:           list of tab keys the parent dashboard exposes (e.g.
 *                     ['schedule','tasks','orders','projects','expenses',...])
 *   - onNavigate:     (tabKey: string) => void  — parent switches its <Tabs>
 *                     to that key. Parent must use controlled Tabs.
 *   - onOpenSchedule: () => void  — opens Schedule tab and triggers Quick
 *                     Builder dialog (parent owns the dialog state).
 *   - clockStatus:    'in' | 'out' — passed straight to the Clock tile hint
 *   - badges:         { [tabKey]: number }  — small notification badges shown
 *                     on the corresponding tile.
 */
import { MobileTileGrid } from './MobileTileGrid';
import {
  Calendar as CalIcon, CheckSquare, Package, ListChecks,
  Building2, Users, BookUser, Briefcase, BarChart3,
  Clock, UserCheck, ClipboardList, MessageSquare,
  Wrench, ArrowLeftRight, RotateCcw, FileText, Receipt,
  TrendingUp, Zap, Activity
} from 'lucide-react';

// Static tile definitions keyed by tab name. Only those whose `key` appears
// in the `tabs` prop will actually render — keeps each dashboard's tile set
// matched to the tabs it actually has.
const TAB_TILES = {
  schedule: { label: 'Schedule', icon: <CalIcon className="w-5 h-5" />, iconBg: '#dbeafe', iconColor: '#1d4ed8' },
  tasks: { label: 'Tasks', icon: <CheckSquare className="w-5 h-5" />, iconBg: '#fef3c7', iconColor: '#b45309' },
  'project-progress': { label: 'Project Progress', icon: <BarChart3 className="w-5 h-5" />, iconBg: '#dcfce7', iconColor: '#15803d' },
  orders: { label: 'Material Orders', icon: <Package className="w-5 h-5" />, iconBg: '#fef3c7', iconColor: '#b45309' },
  projects: { label: 'Projects', icon: <Briefcase className="w-5 h-5" />, iconBg: '#e0e7ff', iconColor: '#4338ca' },
  'view-projects': { label: 'Projects', icon: <Briefcase className="w-5 h-5" />, iconBg: '#e0e7ff', iconColor: '#4338ca' },
  subcontractors: { label: 'Subcontractors', icon: <Wrench className="w-5 h-5" />, iconBg: '#fef9c3', iconColor: '#854d0e' },
  employees: { label: 'Employees', icon: <Users className="w-5 h-5" />, iconBg: '#fae8ff', iconColor: '#86198f' },
  'view-employees': { label: 'Employees', icon: <Users className="w-5 h-5" />, iconBg: '#fae8ff', iconColor: '#86198f' },
  clients: { label: 'Clients', icon: <BookUser className="w-5 h-5" />, iconBg: '#ffe4e6', iconColor: '#9f1239' },
  'view-clients': { label: 'Clients', icon: <BookUser className="w-5 h-5" />, iconBg: '#ffe4e6', iconColor: '#9f1239' },
  equipment: { label: 'Equipment', icon: <Wrench className="w-5 h-5" />, iconBg: '#f3e8ff', iconColor: '#6b21a8' },
  stock: { label: 'Stock', icon: <Package className="w-5 h-5" />, iconBg: '#d1fae5', iconColor: '#047857' },
  'time-tracking': { label: 'Time Tracking', icon: <Clock className="w-5 h-5" />, iconBg: '#dbeafe', iconColor: '#1d4ed8' },
  communication: { label: 'Messages', icon: <MessageSquare className="w-5 h-5" />, iconBg: '#fee2e2', iconColor: '#b91c1c' },
  expenses: { label: 'Expenses', icon: <Receipt className="w-5 h-5" />, iconBg: '#dcfce7', iconColor: '#15803d' },
  exports: { label: 'Excel Exports', icon: <FileText className="w-5 h-5" />, iconBg: '#fef3c7', iconColor: '#b45309' },
  'employee-database': { label: 'Employee DB', icon: <BookUser className="w-5 h-5" />, iconBg: '#ede9fe', iconColor: '#6d28d9' },
  returns: { label: 'Returns', icon: <RotateCcw className="w-5 h-5" />, iconBg: '#fef9c3', iconColor: '#854d0e' },
  transfers: { label: 'Transfers', icon: <ArrowLeftRight className="w-5 h-5" />, iconBg: '#e0e7ff', iconColor: '#4338ca' },
  reconciliation: { label: 'Material Reconciliation', icon: <ListChecks className="w-5 h-5" />, iconBg: '#dcfce7', iconColor: '#15803d' },
  invoices: { label: 'Invoice Management', icon: <Receipt className="w-5 h-5" />, iconBg: '#ede9fe', iconColor: '#6d28d9' },
  payroll: { label: 'Payroll', icon: <TrendingUp className="w-5 h-5" />, iconBg: '#dcfce7', iconColor: '#15803d' },
  reports: { label: 'Reports', icon: <BarChart3 className="w-5 h-5" />, iconBg: '#dbeafe', iconColor: '#1d4ed8' },
};

export function RoleTileGrid({
  role,
  tabs = [],
  onNavigate,
  onOpenScheduleBuilder,
  onOpenClockStatus,
  onOpenProfile,
  onOpenPersonalStats,
  onOpenCompanyStats,
  personalStatsHint,
  companyStatsHint,
  clockStatusLabel,
  badges = {},
}) {
  const personalTiles = [
    // Personal/Company stat tiles — same square size as the rest of the grid.
    // Tapping opens the matching stats "page" above the tile grid. Only
    // included when the parent provides handlers (i.e. the role has payroll
    // visibility).
    ...(onOpenPersonalStats ? [{
      key: 'your-stats',
      label: 'Your Personal Stats',
      hint: personalStatsHint || 'View your hours & pay',
      icon: <Activity className="w-5 h-5" />,
      iconBg: '#d1fae5',
      iconColor: '#047857',
      onClick: onOpenPersonalStats,
    }] : []),
    ...(onOpenCompanyStats ? [{
      key: 'company-stats',
      label: 'Company Total Stats',
      hint: companyStatsHint || 'View company totals',
      icon: <Building2 className="w-5 h-5" />,
      iconBg: '#dbeafe',
      iconColor: '#1e40af',
      onClick: onOpenCompanyStats,
    }] : []),
    {
      key: 'quick-builder',
      label: 'Quick Builder',
      hint: 'Drag-drop schedule',
      icon: <Zap className="w-5 h-5" />,
      iconBg: '#fef3c7',
      iconColor: '#b45309',
      onClick: onOpenScheduleBuilder,
    },
  ];

  const tabTiles = tabs
    .filter((t) => TAB_TILES[t])
    .map((t) => ({
      key: t,
      label: TAB_TILES[t].label,
      icon: TAB_TILES[t].icon,
      iconBg: TAB_TILES[t].iconBg,
      iconColor: TAB_TILES[t].iconColor,
      badge: badges[t] || null,
      badgeColor: badges[t] ? 'amber' : undefined,
      onClick: () => onNavigate?.(t),
    }));

  return (
    <MobileTileGrid
      storageScope={`role-${role}`}
      defaultPins={['your-stats', 'company-stats', 'quick-builder', 'schedule', 'tasks', 'orders']}
      tiles={[...personalTiles, ...tabTiles]}
    />
  );
}

export default RoleTileGrid;
